import { z } from "zod";

export const EMAIL_MAX_LENGTH = 254;
export const PASSWORD_MAX_LENGTH = 256;

export const loginInputSchema = z.object({
  email: z.string().trim().min(1, "Enter your email address.").max(254),
  password: z
    .string()
    .min(1, "Enter your password.")
    .refine((password) => codePointLength(password) <= PASSWORD_MAX_LENGTH),
});

const signupEmailSchema = z
  .string()
  .trim()
  .min(1, "Enter your email address.")
  .max(EMAIL_MAX_LENGTH, "Use 254 characters or fewer.")
  .email("Enter a valid email address.")
  .transform((email) => email.toLocaleLowerCase("en-US"));

export const signupPasswordSchema = z.string().superRefine((password, context) => {
  const length = codePointLength(password);
  if (length === 0) {
    context.addIssue({ code: "custom", message: "Enter a password." });
    return;
  }
  if (length < 8) {
    context.addIssue({ code: "custom", message: "Use at least 8 characters." });
  }
  if (length > PASSWORD_MAX_LENGTH) {
    context.addIssue({ code: "custom", message: "Use 256 characters or fewer." });
  }
  if (!/\p{L}/u.test(password) || !/\p{Nd}/u.test(password)) {
    context.addIssue({
      code: "custom",
      message: "Include at least one letter and one number.",
    });
  }
});

export const signupInputSchema = z.object({
  email: signupEmailSchema,
  password: signupPasswordSchema,
  termsAccepted: z.literal(true, {
    error: "Confirm that you are at least 16 and agree to the Terms of Service.",
  }),
});

export const resendVerificationInputSchema = z.object({
  email: signupEmailSchema,
});

export const verifyEmailInputSchema = z.object({
  token: z
    .string()
    .min(32, "This verification link is invalid.")
    .max(256, "This verification link is invalid.")
    .regex(/^[A-Za-z0-9_-]+$/, "This verification link is invalid."),
  password: signupPasswordSchema,
});

export const signupAcceptedResponseSchema = z.object({
  message: z.string().min(1),
});

export const verifyEmailResponseSchema = z.object({
  verified: z.literal(true),
});

export const authenticatedUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1),
  timeZone: z.string().nullable(),
});

export const sessionResponseSchema = z.object({
  user: authenticatedUserSchema,
});

export const googleCredentialInputSchema = z.object({
  credential: z.string().min(100).max(16_384),
  password: z.string().max(PASSWORD_MAX_LENGTH).optional(),
  termsAccepted: z.literal(true).optional(),
});

export const googleConfigResponseSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().optional(),
});

export const CHANGE_PASSWORD_MAX_LENGTH = 256;

const currentPasswordSchema = passwordField("Enter your current password.");
const newPasswordSchema = passwordField("Enter a new password.");
const confirmNewPasswordSchema = passwordField("Enter your new password again.");

export const changePasswordInputSchema = z
  .object({
    currentPassword: currentPasswordSchema,
    newPassword: newPasswordSchema,
    confirmNewPassword: confirmNewPasswordSchema,
  })
  .superRefine((input, context) => {
    for (const [field, value] of [
      ["currentPassword", input.currentPassword],
      ["newPassword", input.newPassword],
      ["confirmNewPassword", input.confirmNewPassword],
    ] as const) {
      if (codePointLength(value) > CHANGE_PASSWORD_MAX_LENGTH) {
        context.addIssue({
          code: "custom",
          path: [field],
          message: "Use 256 characters or fewer.",
        });
      }
    }
    const newPasswordLength = codePointLength(input.newPassword);
    if (newPasswordLength > 0 && newPasswordLength < 8) {
      context.addIssue({
        code: "custom",
        path: ["newPassword"],
        message: "Use at least 8 characters.",
      });
    }
    if (
      input.newPassword.length > 0 &&
      (!/\p{L}/u.test(input.newPassword) || !/\p{Nd}/u.test(input.newPassword))
    ) {
      context.addIssue({
        code: "custom",
        path: ["newPassword"],
        message: "Include at least one letter and one number.",
      });
    }
    if (input.currentPassword.length > 0 && input.newPassword === input.currentPassword) {
      context.addIssue({
        code: "custom",
        path: ["newPassword"],
        message: "Choose a password different from your current password.",
      });
    }
    if (input.confirmNewPassword.length > 0 && input.newPassword !== input.confirmNewPassword) {
      context.addIssue({
        code: "custom",
        path: ["confirmNewPassword"],
        message: "The new passwords do not match.",
      });
    }
  });

export type LoginInput = z.infer<typeof loginInputSchema>;
export type SignupInput = z.infer<typeof signupInputSchema>;
export type ResendVerificationInput = z.infer<typeof resendVerificationInputSchema>;
export type VerifyEmailInput = z.infer<typeof verifyEmailInputSchema>;
export type SignupAcceptedResponse = z.infer<typeof signupAcceptedResponseSchema>;
export type VerifyEmailResponse = z.infer<typeof verifyEmailResponseSchema>;
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type GoogleCredentialInput = z.infer<typeof googleCredentialInputSchema>;
export type GoogleConfigResponse = z.infer<typeof googleConfigResponseSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;

function passwordField(requiredMessage: string) {
  return z.preprocess(
    (value) => (typeof value === "string" ? value : ""),
    z.string().min(1, requiredMessage),
  );
}

function codePointLength(value: string): number {
  return [...value].length;
}
